// Reading OpenStreetMap's own golf data, straight from Overpass.
//
// The reason this replaced a third-party feature API: the middleman stamped every shape
// with a `course_id` and sometimes a `hole_number`, and both were noise — a course whose
// own record held no green at all, shapes filed under one course sitting six hundred
// yards from any of its holes, two overlapping records for one course with complementary
// numbering. About a thousand lines existed to launder that back into hole identity, and
// a course still took hours.
//
// Overpass makes no identity claims to be wrong about. A bbox query returns the golf
// ways and relations that are there, tagged with what they are, and — crucially —
// `golf=hole` centrelines carrying `ref` and `par`. Measured on North Shore: one keyless
// request, 2.4 seconds, 196 elements, and all eighteen hole lines correctly numbered,
// every one landing within four yards of the green a human had traced by hand.
//
// Pure and DOM-free like the rest of `shared/`, because the parsing rules are the part
// worth testing and the panel around them is not. The fetch itself lives in
// `worker/lib/overpass.ts` — it needs a Worker, a User-Agent and mirror failover.
import type { LatLng, ShapeKind } from "./types.js";
import { centroid } from "./geo.js";

/**
 * A bounding box, south-west to north-east. The same order Overpass wants.
 */
export interface Bounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

/**
 * One element from `out geom;`. Only the fields we read.
 *
 * `out geom` is what makes this workable: every way carries its coordinates inline, and
 * every relation carries its members' coordinates too, so one request is one request. The
 * alternative (`out;` plus node lookups) would be three round trips and a join.
 */
export interface OverpassElement {
  type?: string;
  id?: number;
  tags?: Record<string, string>;
  /** Nodes. */
  lat?: number;
  lon?: number;
  /** Ways. Note `lon`, not `lng` — this is OSM's spelling and the trap below. */
  geometry?: { lat: number; lon: number }[];
  /**
   * Ways and relations under `out bb;` — the bounding box, with no geometry at all.
   *
   * Only the course *lookup* asks for this (see `shared/courseLookup.ts`): finding which
   * club is which needs an extent and a name, and nothing else, so a hundred candidates
   * cost a few kilobytes instead of megabytes of outline nobody draws.
   */
  bounds?: { minlat: number; minlon: number; maxlat: number; maxlon: number };
  /** Relations. */
  members?: {
    type?: string;
    ref?: number;
    role?: string;
    geometry?: { lat: number; lon: number }[];
  }[];
}

/**
 * A normalised golf feature: what everything downstream runs on.
 *
 * Deliberately not the old `OsmFeature`. That interface was shaped around the previous
 * source's wire format — lng-first GeoJSON arrays, `tags` as a string list, an `id` that
 * identified one course record's *copy* of a shape, and a `foreign` flag whose entire job
 * was laundering a course id that no longer exists here. Marshalling Overpass into it
 * would mean encoding lat/lon objects into lng-first pairs so the old code could decode
 * them again, and carrying a flag that is permanently false.
 */
export interface GolfFeature {
  /** "way/695869721". The stable identity of a shape, and the sort key. */
  id: string;
  /** The `golf` tag verbatim: "green", "tee", "hole", "cartpath", … */
  golf: string;
  /** lat-first, with any repeated closing vertex already dropped. */
  points: LatLng[];
  /** Whether `points` enclose anything: a closed way, or a multipolygon. */
  area: boolean;
  center: LatLng;
  /** `tags.ref` on a `golf=hole`, parsed, and only when it names a hole 1–18. */
  ref: number | null;
  par: number | null;
  tags: Record<string, string>;
}

/**
 * What each `golf` value becomes when it is drawn.
 *
 * Everything absent is dropped on purpose. `rough` is the background the diagram already
 * paints; `driving_range` is not part of any hole; `hole` and `pin` are anchors rather
 * than ground, and are consumed by the routing rather than drawn.
 */
export const SHAPE_KINDS: Record<string, ShapeKind> = {
  green: "green",
  tee: "tee",
  fairway: "fairway",
  bunker: "bunker",
  water_hazard: "water",
  lateral_water_hazard: "water",
};

/**
 * The query, as one string.
 *
 * Asserted verbatim in the tests, because this is the contract with a third party and a
 * typo in it does not fail — Overpass answers a malformed-but-valid query with HTTP 200
 * and an empty list, which is the worst shape a failure can take.
 *
 * `way` and `relation` but not `node`: a golf feature with area is one or the other, and
 * the standalone golf nodes (a pin, a bench) are not ground this draws. `out geom;` is
 * what puts the coordinates inline.
 */
export function overpassQuery(b: Bounds): string {
  const box = `${b.south},${b.west},${b.north},${b.east}`;
  return `[out:json][timeout:60];(way["golf"](${box});relation["golf"](${box}););out geom;`;
}

/**
 * Overpass counts lat then lon; every other coordinate in this app is `{lat, lng}`.
 *
 * The single most likely thing to get silently wrong in this file. Reading `lon` as
 * `lat` puts a course in the wrong hemisphere, and every downstream rule still runs
 * happily on it.
 */
function toLatLng(p: { lat?: number; lon?: number } | undefined): LatLng | null {
  if (!p) return null;
  const { lat, lon } = p;
  if (typeof lat !== "number" || typeof lon !== "number") return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lng: lon };
}

function toPoints(geometry: { lat: number; lon: number }[] | undefined): LatLng[] {
  if (!Array.isArray(geometry)) return [];
  const out: LatLng[] = [];
  for (const p of geometry) {
    const ll = toLatLng(p);
    if (ll) out.push(ll);
  }
  return out;
}

/** A closed way repeats its first vertex; our shapes are implicitly closed. */
function dropClosingVertex(points: LatLng[]): { points: LatLng[]; closed: boolean } {
  if (points.length > 1) {
    const a = points[0]!;
    const b = points[points.length - 1]!;
    if (a.lat === b.lat && a.lng === b.lng) return { points: points.slice(0, -1), closed: true };
  }
  return { points, closed: false };
}

/**
 * Twice the signed area of a ring, in square degrees. Only ever compared against another
 * ring on the same course, so the units and the sign don't matter.
 */
export function ringArea(points: LatLng[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    sum += a.lng * b.lat - b.lng * a.lat;
  }
  return Math.abs(sum);
}

/**
 * The outline of a multipolygon relation, as a single ring.
 *
 * Outer members only, largest by **area** — not by point count. Those sound
 * interchangeable and are not: OSM traces a three-yard sliver and a whole fairway with
 * about the same number of vertices, so counting points hands back the sliver and
 * discards the real shape. Inners are the islands inside a shape and are dropped; this
 * draws filled ground, not ground with holes in it.
 *
 * Relations are not an edge case. Barefoot's bbox carries eighteen of them.
 */
function outerRing(el: OverpassElement): LatLng[] {
  let best: LatLng[] = [];
  let bestArea = -1;
  for (const member of el.members ?? []) {
    if (member.role !== "outer") continue;
    const { points } = dropClosingVertex(toPoints(member.geometry));
    if (points.length < 3) continue;
    const area = ringArea(points);
    if (area > bestArea) {
      bestArea = area;
      best = points;
    }
  }
  return best;
}

/** A hole number, or null. Anything outside 1–18 names no hole this app can hold. */
function toHoleNumber(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  // Number("") is 0 and Number(" 7 ") is 7; both are handled by the range test below,
  // but "1A" must not become 1, so this is deliberately not parseInt.
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 18) return null;
  return n;
}

function toPar(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 3 || n > 6) return null;
  return n;
}

/**
 * Overpass elements to golf features.
 *
 * Sorted by id before returning. Overpass makes no promise about element order, and two
 * builds of the same response have to produce byte-identical files — see
 * `shared/mapFile.ts`, whose whole purpose is that a diff means an edit.
 */
export function parseOverpass(elements: OverpassElement[]): GolfFeature[] {
  const out: GolfFeature[] = [];

  for (const el of elements ?? []) {
    const golf = el.tags?.golf;
    if (!golf) continue;
    const id = `${el.type ?? "way"}/${el.id ?? 0}`;
    const tags = el.tags ?? {};

    let points: LatLng[] = [];
    let area = false;

    if (el.type === "relation") {
      points = outerRing(el);
      area = points.length >= 3;
    } else if (el.type === "node") {
      const at = toLatLng(el);
      if (!at) continue;
      points = [at];
      area = false;
    } else {
      const raw = toPoints(el.geometry);
      const closed = dropClosingVertex(raw);
      points = closed.points;
      // `area=yes` is how OSM marks a shape whose ends a mapper never joined. Trusted
      // over the geometry, because it is a statement of intent and the open end is the
      // mistake.
      area = closed.closed || tags["area"] === "yes";
    }

    if (points.length === 0) continue;
    // An area that cannot enclose anything is not an area, whatever it claims.
    if (area && points.length < 3) continue;
    // A line needs two points to have a direction.
    if (!area && points.length < 2 && el.type !== "node") continue;
    // Ground that is meant to be filled — a green, a tee, a fairway, a hazard — is
    // useless as an open line: it cannot be drawn and nothing downstream will look at
    // it. Dropping it here keeps it out of the feature list rather than leaving it to be
    // silently skipped later. `hole`, `cartpath` and friends are legitimately lines.
    if (!area && SHAPE_KINDS[golf]) continue;

    out.push({
      id,
      golf,
      points,
      area,
      center: centroid(points),
      ref: golf === "hole" ? toHoleNumber(tags["ref"]) : null,
      par: golf === "hole" ? toPar(tags["par"]) : null,
      tags,
    });
  }

  out.sort((a, b) => a.id.localeCompare(b.id));
  return out;
}

/** Credit for the data. ODbL requires it wherever the geometry is shown. */
export const OSM_ATTRIBUTION = "© OpenStreetMap contributors";

/** Named beside the credit so the licence the credit refers to is never a guess. */
export const OSM_LICENCE = "ODbL";
